import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Navigation, Bus, Car, Phone } from 'lucide-react';
import { storeInfo } from '../data/mockData';
import SEO from '../components/SEO';

const DirectionsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO
        title="Find Us"
        description="Visit J.B.F. Antiques & Gallery at Doncaster Corn Exchange, DN1 1NJ. Open Wednesdays from 9 AM. Find directions by car, bus, and train."
        path="/directions"
      />
      {/* Header */}
      <section className="py-16 bg-teal-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-serif font-bold mb-4">
            Visit Our Gallery
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto">
            Find us at the historic Doncaster Corn Exchange
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        {/* Map Section */}
        <div className="mb-12">
          <Card className="border-teal-200 shadow-lg overflow-hidden">
            <div className="relative h-[500px] w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2365.8!2d-1.1333!3d53.5244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTPCsDMxJzI3LjgiTiAxwrAwNyc1OS45Ilc!5e0!3m2!1sen!2suk!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="J.B.F. Antiques Location"
              ></iframe>
            </div>
            <CardContent className="p-6 bg-teal-50">
              <div className="flex items-start gap-4">
                <MapPin className="text-teal-700 flex-shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-teal-800 mb-2">Location</h3>
                  <p className="text-gray-700">{storeInfo.address}</p>
                  <a
                    href={storeInfo.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 text-teal-700 hover:text-teal-600 font-medium transition-colors"
                  >
                    <Navigation size={16} />
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Directions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* By Car */}
          <Card className="border-teal-200 hover:shadow-lg transition-shadow">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <Car className="text-teal-700" size={32} />
              </div>
              <h3 className="text-2xl font-serif font-bold text-teal-800 mb-4">
                By Car
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="font-medium">From the M18 Motorway:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Take Junction 3 towards Doncaster (A6182)</li>
                  <li>Follow signs for Town Centre</li>
                  <li>Continue onto Cleveland Street</li>
                  <li>Doncaster Corn Exchange will be on your right</li>
                </ul>
                <div className="mt-4 pt-4 border-t border-teal-200">
                  <p className="font-medium mb-2">Parking:</p>
                  <p className="text-sm">
                    Public car parks available nearby including Waterdale Car Park 
                    and Market Car Park, both within 5 minutes walk.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* By Public Transport */}
          <Card className="border-teal-200 hover:shadow-lg transition-shadow">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <Bus className="text-teal-700" size={32} />
              </div>
              <h3 className="text-2xl font-serif font-bold text-teal-800 mb-4">
                By Public Transport
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="font-medium">From Doncaster Railway Station:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Exit the station and head south on Station Road</li>
                  <li>Turn right onto North Bridge</li>
                  <li>Continue onto Cleveland Street</li>
                  <li>Approximately 10-minute walk (0.5 miles)</li>
                </ul>
                <div className="mt-4 pt-4 border-t border-teal-200">
                  <p className="font-medium mb-2">By Bus:</p>
                  <p className="text-sm">
                    Multiple bus routes serve the town centre. Alight at Market Place 
                    or High Street stops, both within easy walking distance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Information */}
        <Card className="border-teal-200 shadow-lg bg-gradient-to-r from-teal-50 to-cyan-100">
          <CardContent className="p-8">
            <h3 className="text-2xl font-serif font-bold text-teal-800 mb-6 text-center">
              Before You Visit
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-white" size={28} />
                </div>
                <h4 className="font-bold text-teal-800 mb-2">Call Ahead</h4>
                <p className="text-gray-700 text-sm mb-2">
                  We recommend calling before your visit to ensure we're available to assist you.
                </p>
                <a href={`tel:${storeInfo.phone}`} className="text-teal-700 font-medium hover:text-teal-600">
                  {storeInfo.phone}
                </a>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-teal-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-white" size={28} />
                </div>
                <h4 className="font-bold text-teal-800 mb-2">Opening Hours</h4>
                <p className="text-gray-700 text-sm mb-1">
                  {storeInfo.openingHours.days}
                </p>
                <p className="text-gray-600 text-sm">
                  {storeInfo.openingHours.hours}
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-teal-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Navigation className="text-white" size={28} />
                </div>
                <h4 className="font-bold text-teal-800 mb-2">Need Directions?</h4>
                <p className="text-gray-700 text-sm mb-3">
                  Get real-time navigation to our gallery
                </p>
                <a
                  href={storeInfo.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-teal-700 hover:bg-teal-600 text-white">
                    Get Directions
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Landmark Information */}
        <div className="mt-12 text-center max-w-3xl mx-auto">
          <h3 className="text-3xl font-serif font-bold text-teal-800 mb-4">
            About Doncaster Corn Exchange
          </h3>
          <p className="text-gray-700 leading-relaxed">
            The Doncaster Corn Exchange is a Grade II listed building in the heart of Doncaster. 
            Built in 1873, this Victorian architectural gem now houses various independent retailers 
            and antique dealers, making it the perfect home for J.B.F. Antiques & Gallery. 
            The building's historic charm complements our collection beautifully and provides 
            a unique shopping experience for all visitors.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DirectionsPage;
